import { Flex } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { ComponentProps, createContext, useContext, useEffect } from "react";
import { ExtractSlideProps, ISelectorType, SlideProps } from "./types";
import { SelectorState, Snapshot, useQuizActions, useQuizSnapshot } from "../internal/state";
import { useQuizConfig } from "./quizProvider";

const SlideCtx = createContext<SlideProps>(null as any);

export function useSlide() {
  return useContext(SlideCtx);
}

export type QuizQuestionsState = Record<string, Snapshot<SelectorState>>;

export type SlideComponentProps<T extends ISelectorType> = ExtractSlideProps<T> & {
  children?: React.ReactNode;
  containerProps?: ComponentProps<typeof Flex>;
};

export function Slide<T extends ISelectorType>(props: SlideComponentProps<T>) {
  const { children, containerProps, ...slideProps } = props;

  const snap = useQuizSnapshot();
  const actions = useQuizActions();
  const config = useQuizConfig();

  // this effect needs to run for all slides, in order to render the progress bar and etc.
  useEffect(() => {
    actions.registerSlide(slideProps);
  }, []);

  const isActive = slideProps.id === snap.currentSlide?.id;
  const inDuration = config.slideTransitionInDuration ?? 1;
  const outDuration = config.slideTransitionOutDuration ?? 1;

  if (!config.enableSlideTransition) {
    if (!isActive) return null;
    return (
      <SlideCtx.Provider value={slideProps}>
        <CurrentSlide {...props} />
      </SlideCtx.Provider>
    );
  }

  return (
    <AnimatePresence initial={false}>
      {isActive && (
        <motion.div
          key={slideProps.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: inDuration, delay: outDuration } }}
          exit={{ opacity: 0, transition: { duration: outDuration } }}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <SlideCtx.Provider value={slideProps}>
            <CurrentSlide {...props} />
          </SlideCtx.Provider>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CurrentSlide<T extends ISelectorType>(props: SlideComponentProps<T>) {
  const { children, containerProps, ...slideProps } = props;
  const actions = useQuizActions();

  useEffect(() => {
    const slideHeading = document.getElementById("quiz-slide-heading-component");
    actions.updateCurrentSlideDescriptor({
      title: slideHeading?.textContent ?? "",
    });
  }, []);

  return (
    <Flex
      id="slide"
      direction={"column"}
      justifyContent={"start"}
      alignItems={"stretch"}
      minHeight={"100%"}
      width="100vw"
      maxWidth={"430px"}
      height={"100%"}
      py={5}
      px={5}
      {...containerProps}
    >
      {children}
    </Flex>
  );
}
